import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { SPEECHES_WORDS_IN_PLAN } from '@/constants/common';

import { CHARACTERS_PER_GENERATION, formatCount, useMusicT } from './kit';

/** Song generations a month on a plan: its characters ÷ 600. */
const generations = (plan: keyof typeof SPEECHES_WORDS_IN_PLAN) =>
  Math.max(
    1,
    Math.floor(SPEECHES_WORDS_IN_PLAN[plan] / CHARACTERS_PER_GENERATION),
  );

/**
 * How many songs each plan makes. Numbers come from the plan limits, so they
 * follow any change there.
 */
export default function Plans() {
  const { t, lang } = useMusicT();
  const reveal = useReveal<HTMLDivElement>(0.2);

  const stops = [
    {
      plan: 'Free',
      count: generations('Free'),
      note: t('song a month, free'),
    },
    {
      plan: 'Starter',
      count: generations('Starter'),
      note: t('songs a month'),
    },
    {
      plan: 'Unlimited',
      count: generations('Unlimited'),
      note: t('songs a month'),
    },
    {
      plan: 'Unlimited 5000',
      count: generations('Unlimited 5000'),
      note: t('songs a month on our largest plan'),
    },
  ];

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-12 lg:grid-cols-12 lg:items-end'>
          <div className='lg:col-span-7'>
            <SectionHeading
              size='md'
              title={t('Start free.')}
              tail={t('Make more on a paid plan.')}
              lead={t(
                'Every song comes in two versions and uses 600 of your plan’s text-to-speech characters. Songs that fail aren’t counted.',
              )}
            />
          </div>
          <div className='lg:col-span-5 lg:text-end'>
            <Link href='/pricing' className={buttonSecondary}>
              {t('Compare plans')}
              <ArrowGlyph />
            </Link>
          </div>
        </div>

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-[#0f0f28]/[0.1] pt-10 lg:grid-cols-4 dark:border-white/[0.1]',
          )}
        >
          {stops.map((stop, index) => (
            <div
              key={stop.plan}
              className={styles.rv}
              style={delay(index * 90)}
            >
              <p className={clsxm('text-sm font-semibold', tone.quiet)}>
                {t(stop.plan)}
              </p>
              <p
                className={clsxm(
                  styles.display,
                  'mt-3 text-[2.75rem] font-semibold leading-none tabular-nums sm:text-[3.25rem]',
                  index === 0
                    ? 'text-primary-600 dark:text-primary-400'
                    : tone.ink,
                )}
              >
                {formatCount(stop.count, lang)}
              </p>
              <p className={clsxm('mt-3 text-[15px]', tone.body)}>
                {stop.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
